/**
 * Background music for the Be Bold exercise player. Loops a soft
 * track under the session so the squeeze / release rhythm has
 * something to ride on, instead of training in silence.
 *
 * Design:
 *   - Default ON. Mirrors `userProgress.music_enabled` (defaults
 *     true; explicit false from Profile turns it off).
 *   - Plays `/audio/music/{track}.mp3`. If the file is absent the
 *     play call silently no-ops, so the feature is safe to ship
 *     before any audio is committed.
 *   - One looping HTMLAudioElement at a time. Linear fade in/out
 *     via gain ramping on a WebAudio MediaElementSource so the
 *     start of an exercise doesn't pop and the end doesn't cut.
 *   - All operations wrap in try/catch — a missing file or a
 *     suspended AudioContext never breaks the player.
 */

const DEFAULT_TRACK = 'flow';
const FADE_IN_MS = 900;
const FADE_OUT_MS = 700;
const TARGET_VOLUME = 0.35;

let _enabled = true;
let _audio = null;
let _audioCtx = null;
let _gainNode = null;
let _activeTrack = null;
// Probe cache so we don't re-HEAD the same file on every session.
const _probeCache = new Map();

export function setMusicEnabled(on) {
  _enabled = !!on;
  if (!on) stop();
}

export function isMusicEnabled() {
  return _enabled;
}

function getCtx() {
  if (_audioCtx) return _audioCtx;
  const AC = typeof window !== 'undefined'
    ? (window.AudioContext || window.webkitAudioContext)
    : null;
  if (!AC) return null;
  try { _audioCtx = new AC(); } catch { _audioCtx = null; }
  return _audioCtx;
}

// Probe a URL to confirm it's actually an audio file. A naïve HEAD
// check is unreliable: SPA hosts (Vite dev server, Netlify, etc.)
// answer 200 for unknown paths with the index.html fallback, so a
// missing flow.mp3 looks "present" and we'd hand HTML to the
// decoder. We require the response to (a) succeed and (b) advertise
// an audio Content-Type. Server misconfigs that omit the header still
// fail safe — we'd skip the file and try the next extension.
async function probe(url) {
  if (_probeCache.has(url)) return _probeCache.get(url);
  const promise = fetch(url, { method: 'HEAD' })
    .then((r) => {
      if (!r.ok) return false;
      const ct = (r.headers.get('content-type') || '').toLowerCase();
      return ct.startsWith('audio/');
    })
    .catch(() => false);
  _probeCache.set(url, promise);
  return promise;
}

/**
 * Start (or switch to) a looping background track. Fades in over
 * FADE_IN_MS so the entry is gentle. A second call with the same
 * track is a no-op — useful for re-mounts where the player toggles
 * exercise → transition → exercise without us wanting to restart
 * the music each time.
 *
 * Probes `.mp3` first (preferred for size). Falls back to `.wav` so
 * a synthesised placeholder (or a recorded WAV that hasn't been
 * encoded to MP3 yet) still plays. Both extensions point at the same
 * file slot under /audio/music/ — first one that exists wins.
 *
 * @param {string} [track]  filename under /audio/music/, no extension
 */
export async function play(track = DEFAULT_TRACK) {
  if (!_enabled) return;
  if (typeof window === 'undefined') return;
  if (_activeTrack === track && _audio && !_audio.paused) return;

  let url = `/audio/music/${track}.mp3`;
  if (!(await probe(url))) {
    url = `/audio/music/${track}.wav`;
    if (!(await probe(url))) return;
  }

  try {
    if (_audio) {
      try { _audio.pause(); } catch {}
      _audio = null;
    }
    const a = new Audio(url);
    a.loop = true;
    a.preload = 'auto';
    a.crossOrigin = 'anonymous';

    const ctx = getCtx();
    if (ctx) {
      try {
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const src = ctx.createMediaElementSource(a);
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(
          TARGET_VOLUME,
          ctx.currentTime + FADE_IN_MS / 1000,
        );
        src.connect(gain).connect(ctx.destination);
        _gainNode = gain;
      } catch {
        // CORS / cross-origin can refuse createMediaElementSource. Fall
        // back to plain <audio> volume — no fade, but the music plays.
        a.volume = TARGET_VOLUME;
        _gainNode = null;
      }
    } else {
      a.volume = TARGET_VOLUME;
    }

    _audio = a;
    _activeTrack = track;
    a.play().catch(() => {
      // Autoplay policy can refuse. A user gesture (tap Begin) usually
      // resumes the AudioContext; if it doesn't, music just stays
      // silent for this session.
      _audio = null;
      _activeTrack = null;
    });
  } catch {
    _audio = null;
    _activeTrack = null;
  }
}

/**
 * Fade the current track out over FADE_OUT_MS, then pause + release.
 * Safe to call when nothing is playing.
 */
export function stop() {
  const a = _audio;
  if (!a) return;
  const ctx = _audioCtx;
  const gain = _gainNode;
  _audio = null;
  _activeTrack = null;
  _gainNode = null;
  try {
    if (ctx && gain) {
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + FADE_OUT_MS / 1000);
      setTimeout(() => { try { a.pause(); a.src = ''; } catch {} }, FADE_OUT_MS + 60);
    } else {
      try { a.pause(); a.src = ''; } catch {}
    }
  } catch {
    try { a.pause(); } catch {}
  }
}

/** Pause without fade — used on tab-hide. resume() picks back up. */
export function pause() {
  if (_audio && !_audio.paused) {
    try { _audio.pause(); } catch {}
  }
}

export function resume() {
  if (!_enabled) return;
  if (_audio && _audio.paused) {
    _audio.play().catch(() => {});
  }
}
